import React, { useState, useRef, useEffect } from 'react';
import { aiService } from '../services/aiService';
import { backendService } from '../services/backendService';

interface VoiceCommandPanelProps {
  onClose: () => void;
  addLog: (level: 'INFO' | 'WARN' | 'ERROR' | 'AI_ACTION', message: string, component?: string) => void;
}

const VoiceCommandPanel: React.FC<VoiceCommandPanelProps> = ({ onClose, addLog }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [recordingTime, setRecordingTime] = useState(0);
  const [commandHistory, setCommandHistory] = useState<Array<{ command: string, timestamp: string, result?: any }>>([]);
  const ongoingPlayback = useRef<AudioBufferSourceNode | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Initialize audio context for visualization
  useEffect(() => {
    const initAudioContext = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

        // Only resume if suspended (don't try to resume immediately)
        if (audioContext.state === 'suspended') {
          // Defer resume until user interaction
          const resumeOnInteraction = () => {
            if (audioContext.state === 'suspended') {
              audioContext.resume().catch(err => console.warn('Failed to resume audio context:', err));
            }
            // Remove listeners after first interaction
            document.removeEventListener('click', resumeOnInteraction);
            document.removeEventListener('touchstart', resumeOnInteraction);
            document.removeEventListener('keydown', resumeOnInteraction);
          };

          document.addEventListener('click', resumeOnInteraction);
          document.addEventListener('touchstart', resumeOnInteraction);
          document.addEventListener('keydown', resumeOnInteraction);
        }

        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(stream);

        analyser.fftSize = 256;
        microphone.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
      } catch (error) {
        console.error('Error initializing audio context:', error);
        addLog('ERROR', 'Failed to initialize audio recording: ' + (error as Error).message, 'VoiceCommand');
      }
    };

    initAudioContext();

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Audio level visualization
  const updateAudioLevel = () => {
    if (analyserRef.current && isRecording) {
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(dataArray);

      // Calculate average volume
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      setAudioLevel(average / 255); // Normalize to 0-1

      animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        await processAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      // Start audio visualization
      updateAudioLevel();

      addLog('INFO', 'Voice recording started', 'VoiceCommand');
    } catch (error) {
      console.error('Error starting recording:', error);
      addLog('ERROR', 'Failed to start recording: ' + (error as Error).message, 'VoiceCommand');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      setAudioLevel(0);
      addLog('INFO', 'Voice recording stopped', 'VoiceCommand');
    }
  };

  const ttsSpeak = async (text: string) => {
    if (!audioContextRef.current) return;
    try {
      const audioData = await aiService.synthesizeSpeech(text, 'pl');
      if (audioData) {
        const buffer = await audioContextRef.current.decodeAudioData(audioData);
        const source = audioContextRef.current.createBufferSource();
        ongoingPlayback.current = source;
        source.buffer = buffer;
        source.connect(audioContextRef.current.destination);
        source.start();
      }
    } catch (e) {
      console.error("TTS Speak failed:", e);
    }
  };

  const executeVoiceCommand = async (command: string) => {
    try {
      const lowerCmd = command.toLowerCase();
      // 1. Snapshot commands — FIX #3: Cross-applet camera bridge
      if (/co widzisz|snapshot|zrób zdjęcie|co jest na ekranie/i.test(lowerCmd)) {
        const snapshotFn = (window as any).nexusSnapshot;
        if (!snapshotFn) {
          return { text: "Kamera nie jest aktywna. Włącz najpierw tryb Vision Observer." };
        }
        try {
          addLog('AI_ACTION', 'Snapshot: przechwytywanie klatki z kamery...', 'VoiceCommand');
          const snap = await snapshotFn();
          if (snap.error) {
            return { text: `Nie udało się przechwycić obrazu: ${snap.error}` };
          }
          addLog('INFO', 'Snapshot przechwycony, wysyłam do analizy wizyjnej...', 'VoiceCommand');
          const result = await backendService.analyzeImage(snap.base64, command);
          const description = typeof result === 'string' ? result : (result as any)?.description || 'Analiza zakończona';
          return { text: description };
        } catch (e) {
          console.error('Snapshot analysis failed:', e);
          return { text: "Błąd analizy obrazu z kamery." };
        }
      }
      // 2. System commands
      if (/status|metryki|system/i.test(lowerCmd)) {
        return { text: "Pobieram metryki systemowe." };
      }
      // 3. MCP tool commands
      if (/uruchom|wykonaj|otwórz/i.test(lowerCmd)) {
        return { text: `Inicjuję komendę systemową: ${command}` };
      }
      // 4. General chat (fallback do Agenta - symulacja uproszczona)
      return { text: `Otrzymałem polecenie: ${command}. Oczekuję na przetworzenie.` };
    } catch (e) {
      return { error: 'Execution failed', text: 'Błąd wykonywania komendy.' };
    }
  };

  const processAudio = async (audioBlob: Blob) => {
    setIsProcessing(true);
    setTranscript(''); // Clear previous transcript

    try {
      addLog('INFO', 'Processing voice command...', 'VoiceCommand');

      // Show progress indicator with stages
      setTranscript('🎙️ Initializing audio processing...');
      await new Promise(resolve => setTimeout(resolve, 500));

      setTranscript('🔄 Connecting to ASR service...');
      await new Promise(resolve => setTimeout(resolve, 300));

      setTranscript('🔊 Transcribing audio content...');

      // Transcribe audio
      const transcription = await aiService.transcribeAudio(audioBlob);

      // Enhanced response handling with better error detection
      let finalTranscription = '';
      let hasError = false;

      if (typeof transcription === 'string') {
        finalTranscription = transcription.trim();
        // Enhanced error detection
        if (finalTranscription.includes('ASR Error:') ||
          finalTranscription.includes('Error:') ||
          finalTranscription.includes('NetworkError') ||
          finalTranscription.includes('Connection failed') ||
          finalTranscription.includes('timeout') ||
          finalTranscription.includes('unavailable')) {
          hasError = true;
          finalTranscription = finalTranscription
            .replace('ASR Error:', '')
            .replace('Error:', '')
            .replace(/^NetworkError:?\s*/i, '')
            .trim();
        }
      } else if (transcription && typeof transcription === 'object') {
        const transcriptionObj = transcription as Record<string, any>;
        if (transcriptionObj.error) {
          hasError = true;
          finalTranscription = transcriptionObj.error;
        } else {
          finalTranscription = transcriptionObj.response || transcriptionObj.text || JSON.stringify(transcription);
        }
      } else if (transcription === null || transcription === undefined) {
        hasError = true;
        finalTranscription = 'ASR service returned empty response';
      }

      // Check for empty or invalid responses
      if (!hasError && (!finalTranscription ||
        finalTranscription.includes('Empty response') ||
        finalTranscription.length < 2)) {
        hasError = true;
        finalTranscription = 'No speech detected or transcription failed';
      }

      if (hasError) {
        setTranscript(`❌ ${finalTranscription}`);
        addLog('ERROR', `Voice transcription failed: ${finalTranscription}`, 'VoiceCommand');
      } else if (finalTranscription) {
        setTranscript(`✅ ${finalTranscription}`);

        // Execute command
        const execResult = await executeVoiceCommand(finalTranscription);

        setCommandHistory(prev => [...prev, {
          command: finalTranscription,
          timestamp: new Date().toLocaleTimeString(),
          result: execResult
        }]);

        addLog('AI_ACTION', `Voice command transcribed: "${finalTranscription}"`, 'VoiceCommand');
        addLog('INFO', `Voice command executed. Result: ${execResult.text}`, 'VoiceCommand');

        if (execResult.text) {
          await ttsSpeak(execResult.text);
        }
      }
    } catch (error) {
      console.error('Error processing audio:', error);
      const errorMessage = (error as Error).message || 'Unknown transcription error';
      setTranscript(`❌ Transcription failed: ${errorMessage}`);
      addLog('ERROR', 'Failed to process voice command: ' + errorMessage, 'VoiceCommand');
    } finally {
      setIsProcessing(false);
    }
  };

  const clearTranscript = () => {
    setTranscript('');
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#1e222d] border border-[#2a2e39] rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-[#2a2e39]">
          <div>
            <h2 className="text-xl font-black text-white uppercase italic tracking-tighter">
              Voice <span className="text-purple-500">Command</span> Interface
            </h2>
            <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-1">
              Neural Audio Processing System
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-white transition-colors"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recording Section */}
            <div className="space-y-6">
              <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-6">
                <h3 className="text-[12px] font-black text-slate-500 uppercase tracking-widest mb-4">
                  Audio Capture
                </h3>

                {/* Audio Visualization */}
                <div className="mb-6">
                  <div className="h-24 bg-[#0f1115] rounded-lg mb-4 relative overflow-hidden">
                    <div
                      className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-purple-500 to-blue-500 transition-all duration-100"
                      style={{ height: `${audioLevel * 100}%` }}
                    ></div>
                    {[...Array(32)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute bottom-0 w-2 bg-slate-700/30"
                        style={{
                          left: `${(i * 100) / 31}%`,
                          height: `${20 + Math.random() * 60}%`
                        }}
                      ></div>
                    ))}
                  </div>
                  <div className="text-center">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black uppercase ${isRecording
                      ? 'bg-red-500/20 text-red-400 border border-red-500/30 animate-pulse'
                      : 'bg-slate-700/20 text-slate-500 border border-slate-700/30'
                      }`}>
                      <i className={`fas ${isRecording ? 'fa-microphone' : 'fa-microphone-alt-slash'} mr-2`}></i>
                      {isRecording ? 'RECORDING' : 'READY'}
                    </div>
                    {isRecording && (
                      <div className="mt-2 text-[10px] text-slate-500 font-mono">
                        {formatTime(recordingTime)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Controls */}
                <div className="flex gap-3">
                  {!isRecording ? (
                    <button
                      onClick={startRecording}
                      disabled={isProcessing}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-black py-3 px-4 rounded-lg transition-all uppercase text-[10px] tracking-wider shadow-lg hover:shadow-purple-500/20 disabled:opacity-50"
                    >
                      <i className="fas fa-microphone mr-2"></i>
                      Start Recording
                    </button>
                  ) : (
                    <button
                      onClick={stopRecording}
                      className="flex-1 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-black py-3 px-4 rounded-lg transition-all uppercase text-[10px] tracking-wider shadow-lg hover:shadow-red-500/20"
                    >
                      <i className="fas fa-stop mr-2"></i>
                      Stop Recording
                    </button>
                  )}

                  <button
                    onClick={clearTranscript}
                    disabled={!transcript}
                    className="px-4 py-3 bg-slate-700/20 hover:bg-slate-600/20 text-slate-400 hover:text-white border border-slate-700/30 hover:border-slate-600/30 rounded-lg transition-all uppercase text-[10px] font-black tracking-wider disabled:opacity-50"
                  >
                    <i className="fas fa-trash mr-2"></i>
                    Clear
                  </button>
                </div>
              </div>

              {/* Transcript Display with Enhanced Status */}
              <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-[12px] font-black text-slate-500 uppercase tracking-widest">
                    Transcription Result
                  </h3>
                  <div className={`flex items-center gap-2 text-[9px] font-black ${isProcessing ? 'text-yellow-400' :
                    transcript?.startsWith('✅') ? 'text-green-400' :
                      transcript?.startsWith('❌') ? 'text-red-400' : 'text-slate-500'
                    }`}>
                    <i className={`fas ${isProcessing ? 'fa-spinner fa-spin' :
                      transcript?.startsWith('✅') ? 'fa-check-circle' :
                        transcript?.startsWith('❌') ? 'fa-exclamation-circle' : 'fa-microphone'
                      }`}></i>
                    {isProcessing ? 'PROCESSING' :
                      transcript?.startsWith('✅') ? 'SUCCESS' :
                        transcript?.startsWith('❌') ? 'ERROR' : 'READY'}
                  </div>
                </div>
                <div className="min-h-[150px] bg-[#0f1115] rounded-lg p-4 border border-[#2a2e39] relative">
                  {isProcessing ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500 mx-auto mb-3"></div>
                        <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-3">Transcribing Audio</p>
                        <div className="w-full bg-[#2a2e39] rounded-full h-2 mb-2">
                          <div className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full animate-pulse" style={{ width: '85%' }}></div>
                        </div>
                        <p className="text-[9px] text-slate-600">Analyzing speech patterns...</p>
                      </div>
                    </div>
                  ) : transcript ? (
                    <div className={`${transcript.startsWith('✅') ? 'text-green-300' : transcript.startsWith('❌') ? 'text-red-300' : 'text-slate-300'} text-[12px] leading-relaxed font-medium`}>
                      <div className="font-mono whitespace-pre-wrap break-words">
                        {transcript}
                      </div>
                      {transcript.startsWith('❌') && (
                        <div className="mt-4 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
                          <div className="flex items-center gap-2 text-red-400 text-[10px] mb-2">
                            <i className="fas fa-info-circle"></i>
                            <span className="font-bold uppercase">Troubleshooting Tips</span>
                          </div>
                          <ul className="text-[9px] text-red-300 space-y-1">
                            <li>• Check microphone permissions</li>
                            <li>• Ensure clear audio environment</li>
                            <li>• Speak clearly and at normal volume</li>
                            <li>• Verify ASR service connectivity</li>
                          </ul>
                        </div>
                      )}
                      {transcript.startsWith('✅') && (
                        <div className="mt-4 p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
                          <div className="flex items-center gap-2 text-green-400 text-[10px]">
                            <i className="fas fa-check"></i>
                            <span className="font-bold uppercase">Ready for execution</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-600">
                      <div className="text-center">
                        <i className="fas fa-comment-dots text-3xl mb-3 opacity-20"></i>
                        <p className="text-[10px] uppercase tracking-widest mb-1">Awaiting Voice Input</p>
                        <p className="text-[9px] text-slate-500">Record audio to begin transcription</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Command History */}
            <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-6">
              <h3 className="text-[12px] font-black text-slate-500 uppercase tracking-widest mb-4">
                Command History
              </h3>
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {commandHistory.length === 0 ? (
                  <div className="text-center py-12 text-slate-600">
                    <i className="fas fa-history text-3xl mb-3 opacity-20"></i>
                    <p className="text-[10px] uppercase tracking-widest">No commands recorded</p>
                  </div>
                ) : (
                  commandHistory.map((cmd, index) => (
                    <div key={index} className="bg-[#1e222d] border border-[#2a2e39] rounded p-3">
                      <div className="text-[10px] text-slate-500 mb-1 font-mono flex justify-between">
                        <span>{cmd.timestamp}</span>
                        {cmd.result && <span className="text-blue-400">EXECUTED</span>}
                      </div>
                      <div className="text-slate-300 text-[11px] leading-relaxed font-bold">
                        {cmd.command}
                      </div>
                      {cmd.result?.text && (
                        <div className="mt-2 text-[10px] text-slate-400 border-l-2 border-purple-500 pl-2 italic">
                          {cmd.result.text}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#2a2e39] bg-[#1a1e27] rounded-b-xl">
          <div className="flex justify-between items-center text-[10px] text-slate-500 uppercase tracking-widest">
            <div className="flex items-center gap-4">
              <span>Status: <span className={isRecording ? 'text-red-400' : isProcessing ? 'text-yellow-400' : 'text-green-400'}>
                {isRecording ? 'Recording' : isProcessing ? 'Processing' : 'Ready'}
              </span></span>
              <span>Commands: {commandHistory.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="fas fa-circle text-green-500 animate-pulse"></i>
              <span>Neural Audio Link Active</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceCommandPanel;